import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { rfpListings, actionRFPByAdmin } from '../../util/api';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { setCurrentUser } from '../../ducks/user.duck';
import { parse } from '../../util/urlHelpers';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 100;

// ================ Helper functions ================ //

const resultIds = data => data.data.map(l => l.id);
const entityRefs = entities =>
    entities.map(entity => ({
        id: entity.id,
        type: entity.type,
    }));

// ================ Async thunks ================ //

//////////////////////////////
// Overtime Bill Draft Data //
//////////////////////////////
export const saveOvertimeBillDraftThunk = createAsyncThunk(
  'EventProviderDashboardPage/saveOvertimeBillDraft',
  ({ transactionId, draft, storageKey = 'overtimeBillDrafts' }, { dispatch, rejectWithValue, extra: sdk }) => {
    if (!transactionId) {
      return rejectWithValue(storableError(new Error('Missing transactionId')));
    }

    const queryParams = {
      expand: true,
      include: ['profileImage', 'stripeAccount'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .show({ expand: false })
      .then(showResp => {
        const entities = denormalisedResponseEntities(showResp);
        const currentUser = entities.length === 1 ? entities[0] : null;
        const existingPrivateData = currentUser?.attributes?.profile?.privateData;

        const draftsMap =
          typeof existingPrivateData?.[storageKey] === 'object' && existingPrivateData?.[storageKey] !== null
            ? existingPrivateData[storageKey]
            : {};

        const nowIso = new Date().toISOString();
        const existingDraft = draftsMap?.[transactionId];
        const createdAt = existingDraft?.createdAt || draft?.createdAt || nowIso;
        const nextDraft = {
          ...draft,
          createdAt,
          updatedAt: nowIso,
        };

        const nextDraftsMapBase = {
          ...draftsMap,
          [transactionId]: nextDraft,
        };

        const entries = Object.entries(nextDraftsMapBase);
        const sorted = entries.sort((a, b) => {
          const aCreated = a?.[1]?.createdAt ? new Date(a[1].createdAt).getTime() : 0;
          const bCreated = b?.[1]?.createdAt ? new Date(b[1].createdAt).getTime() : 0;
          return aCreated - bCreated;
        });

        const overflow = Math.max(sorted.length - 5, 0);
        const trimmed = overflow > 0 ? sorted.slice(overflow) : sorted;
        const nextDraftsMap = trimmed.reduce((acc, [id, value]) => {
          acc[id] = value;
          return acc;
        }, {});

        const actionPayload = {
          privateData: {
            ...(typeof existingPrivateData === 'object' && existingPrivateData !== null ? existingPrivateData : {}),
            [storageKey]: nextDraftsMap,
          },
        };

        return sdk.currentUser.updateProfile(actionPayload, queryParams);
      })
      .then(updateResp => {
        const entities = denormalisedResponseEntities(updateResp);
        if (entities.length === 1) {
          dispatch(setCurrentUser(entities[0]));
        }
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);

export const saveOvertimeBillDraft = (transactionId, draft, storageKey) => dispatch => {
  return dispatch(saveOvertimeBillDraftThunk({ transactionId, draft, storageKey })).unwrap();
};

export const deleteOvertimeBillDraftThunk = createAsyncThunk(
  'EventProviderDashboardPage/deleteOvertimeBillDraft',
  ({ transactionId, storageKey = 'overtimeBillDrafts' }, { dispatch, rejectWithValue, extra: sdk }) => {
    if (!transactionId) {
      return rejectWithValue(storableError(new Error('Missing transactionId')));
    }

    const queryParams = {
      expand: true,
      include: ['profileImage', 'stripeAccount'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .show({ expand: false })
      .then(showResp => {
        const entities = denormalisedResponseEntities(showResp);
        const currentUser = entities.length === 1 ? entities[0] : null;
        const existingPrivateData = currentUser?.attributes?.profile?.privateData;

        const draftsMap =
          typeof existingPrivateData?.[storageKey] === 'object' && existingPrivateData?.[storageKey] !== null
            ? existingPrivateData[storageKey]
            : {};

        if (!draftsMap?.[transactionId]) {
          return true;
        }

        const nextDraftsMap = { ...draftsMap };
        delete nextDraftsMap[transactionId];

        const actionPayload = {
          privateData: {
            ...(typeof existingPrivateData === 'object' && existingPrivateData !== null ? existingPrivateData : {}),
            [storageKey]: nextDraftsMap,
          },
        };

        return sdk.currentUser.updateProfile(actionPayload, queryParams);
      })
      .then(updateResp => {
        if (updateResp === true) {
          return true;
        }
        const entities = denormalisedResponseEntities(updateResp);
        if (entities.length === 1) {
          dispatch(setCurrentUser(entities[0]));
        }
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  }
);

export const deleteOvertimeBillDraft = (transactionId, storageKey) => dispatch => {
  return dispatch(deleteOvertimeBillDraftThunk({ transactionId, storageKey })).unwrap();
};

export const queryProviderListings = createAsyncThunk(
    'EventProviderDashboardPage/queryProviderListings',
    async ({ queryParams, activeListingId }, { dispatch, getState }) => {
        console.log('inside queryProviderListings, queryParams:', queryParams, 'activeListingId:', activeListingId);
        
        const { listingId = activeListingId, tab, bidType, ...rest } = queryParams;
        const payload = { ...rest, bidType, listingId };
        const { currentUser } = getState().user;
        const currentUserId = currentUser && currentUser.id && currentUser.id.uuid;
        const { isAdmin } = (currentUserId && currentUser.attributes.profile.publicData) || {};

        if (!isAdmin || tab != 'admin') {
            Object.assign(payload, { authorId: currentUserId });
        } else {
            Object.assign(payload, { tab });
        }

        try {
            const res = await rfpListings(payload);
            console.log(res, ' ------res');
            if (res && res.data) {
                const {
                    headerCount = {},
                    transactions,
                    listingsRFP,
                    reviews,
                    matchedBusiness,
                    successData = {},
                } = res.data;

                if (headerCount && Object.keys(headerCount).length) {
                    Object.assign(successData, { headerCount });
                }
                if (reviews) {
                    Object.assign(successData, { reviews });
                }
                if (listingsRFP) {
                    dispatch(addMarketplaceEntities({ data: listingsRFP }));
                    Object.assign(successData, {
                        pagination: listingsRFP.meta,
                        rfpIds: resultIds(listingsRFP),
                    });
                }
                if (matchedBusiness && matchedBusiness.length) {
                    Object.assign(successData, { matchedBusiness });
                } else {
                    Object.assign(successData, { matchedBusiness: [] });
                }
                if (transactions) {
                    dispatch(addMarketplaceEntities({ data: transactions }));
                    Object.assign(successData, { transactionIds: entityRefs(transactions.data) });
                } else {
                    Object.assign(successData, { transactionIds: [] });
                }
                console.log(successData, ' ------successData');
                return successData;
            }
            return res;
        } catch (e) {
            console.error(e, '**** queryProviderListings **** => e');
            throw storableError(e);
        }
    }
);

export const actionRFPListingByAdmin = createAsyncThunk(
    'EventProviderDashboardPage/actionRFPListingByAdmin',
    async ({ payload, activeListingId }, { dispatch }) => {
        try {
            const { queryParams, ...rest } = payload;
            const res = await actionRFPByAdmin(rest);
            await dispatch(queryProviderListings({ queryParams, activeListingId }));
            return res;
        } catch (err) {
            console.error(err, '=== ===> err');
            throw err;
        }
    }
);

// ================ Slice ================ //

const initialState = {
    pagination: null,
    queryParams: null,
    queryInProgress: false,
    queryListingsError: null,
    headerCount: {},
    rfpIds: [],
    reviews: [],
    transactionIds: [],
    matchedBusiness: [],
    activeListingId: null,
    activeListingInProgress: false,
};

const eventProviderDashboardSlice = createSlice({
    name: 'EventProviderDashboardPage',
    initialState,
    reducers: {
        setActiveListingId: (state, action) => {
            state.reviews = [];
            state.activeListingInProgress = true;
            state.activeListingId = action.payload;
        },
        setQueryParams: (state, action) => {
            state.queryParams = action.payload;
            state.queryInProgress = true;
            state.queryListingsError = null;
            state.headerCount = {};
            state.rfpIds = [];
            state.reviews = [];
            state.transactionIds = [];
            state.matchedBusiness = [];
        },
    },
    extraReducers: builder => {
        builder
            // queryProviderListings cases
            .addCase(queryProviderListings.pending, (state, action) => {
                const { queryParams } = action.meta.arg;
                state.queryParams = queryParams;
                state.queryInProgress = true;
                state.queryListingsError = null;
                state.headerCount = {};
                state.rfpIds = [];
                state.reviews = [];
                state.transactionIds = [];
                state.matchedBusiness = [];
            })
            .addCase(queryProviderListings.fulfilled, (state, action) => {
                return {
                    ...state,
                    ...action.payload,
                    queryInProgress: false,
                    queryListingsError: null,
                    activeListingInProgress: false,
                };
            })
            .addCase(queryProviderListings.rejected, (state, action) => {
                console.error(action.error);
                state.queryInProgress = false;
                state.activeListingInProgress = false;
                state.queryListingsError = action.error;
            })
            // actionRFPListingByAdmin cases
            .addCase(actionRFPListingByAdmin.pending, state => {
                // No specific state changes needed for pending
            })
            .addCase(actionRFPListingByAdmin.fulfilled, state => {
                // No specific state changes needed for fulfilled
                // The queryProviderListings will handle the state updates
            })
            .addCase(actionRFPListingByAdmin.rejected, (state, action) => {
                console.error(action.error);
                state.queryListingsError = action.error;
            });
    },
});

export const { setActiveListingId, setQueryParams } = eventProviderDashboardSlice.actions;
export default eventProviderDashboardSlice.reducer;

// ================ Backward compatibility thunks ================ //

export const queryListingsRequest = queryParams => setQueryParams(queryParams);
export const queryListingsSuccess = response => ({ type: 'DUMMY_SUCCESS', payload: response });
export const queryListingsError = e => ({ type: 'DUMMY_ERROR', payload: e });

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
    return dispatch(fetchCurrentUser())
        .then(currentUser => currentUser)
        .catch(e => e);
};